import type {
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireItemEnableWhen,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';

import { getAnswerOptionDisplayLabel } from './answerOptionLabel';

/** Marks a nested string item as a per-option follow-up on a choice question. */
export const OPTION_ADDON_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/questionnaire-option-addon';

export function supportsPerOptionAddons(item: QuestionnaireItem): boolean {
  return item.type === 'choice' || item.type === 'open-choice';
}

export function isOptionAddonItem(item: QuestionnaireItem): boolean {
  return item.extension?.some((e) => e.url === OPTION_ADDON_EXTENSION) ?? false;
}

export function optionAddonKey(option: QuestionnaireItemAnswerOption, index: number): string {
  return option.id ?? String(index);
}

export function buildOptionAddonLinkId(
  parentLinkId: string,
  option: QuestionnaireItemAnswerOption,
  index: number,
): string {
  const key = optionAddonKey(option, index).replace(/[^A-Za-z0-9.-]/g, '-');
  return `${parentLinkId}-opt-${key}-addon`;
}

export function buildEnableWhenForOption(
  parent: QuestionnaireItem,
  option: QuestionnaireItemAnswerOption,
  index: number,
): QuestionnaireItemEnableWhen {
  const base: QuestionnaireItemEnableWhen = {
    question: parent.linkId ?? '',
    operator: '=',
  };

  if (option.valueCoding?.code) {
    return {
      ...base,
      answerCoding: {
        ...option.valueCoding,
        display: option.valueCoding.display ?? getAnswerOptionDisplayLabel(option),
      },
    };
  }

  return {
    ...base,
    answerString: getAnswerOptionDisplayLabel(option) || `Option ${index + 1}`,
  };
}

function enableWhenMatchesOption(
  condition: QuestionnaireItemEnableWhen | undefined,
  parent: QuestionnaireItem,
  option: QuestionnaireItemAnswerOption,
  index: number,
): boolean {
  if (!condition || condition.question !== parent.linkId || condition.operator !== '=') {
    return false;
  }

  if (option.valueCoding?.code) {
    return condition.answerCoding?.code === option.valueCoding.code;
  }

  const label = getAnswerOptionDisplayLabel(option) || `Option ${index + 1}`;
  return condition.answerString === label;
}

export function getOptionIndexForAddon(
  parent: QuestionnaireItem,
  addon: QuestionnaireItem,
): number {
  const marker = addon.extension?.find((e) => e.url === OPTION_ADDON_EXTENSION)?.valueString;
  const options = parent.answerOption ?? [];

  if (marker) {
    const byId = options.findIndex((opt, idx) => optionAddonKey(opt, idx) === marker);
    if (byId >= 0) return byId;
  }

  const condition = addon.enableWhen?.[0];
  return options.findIndex((opt, idx) => enableWhenMatchesOption(condition, parent, opt, idx));
}

export function getOptionAddon(
  parent: QuestionnaireItem,
  optionIndex: number,
): QuestionnaireItem | undefined {
  const option = parent.answerOption?.[optionIndex];
  if (!option) return undefined;

  return (parent.item ?? []).find((child) => {
    if (!isOptionAddonItem(child)) return false;
    return getOptionIndexForAddon(parent, child) === optionIndex;
  });
}

export function getOptionAddonPlaceholder(
  parent: QuestionnaireItem,
  optionIndex: number,
): string | null {
  const addon = getOptionAddon(parent, optionIndex);
  if (!addon) return null;
  return addon.text ?? '';
}

export function getChoiceOptionAddons(parent: QuestionnaireItem): QuestionnaireItem[] {
  return (parent.item ?? []).filter(isOptionAddonItem);
}

/** Depth-first map of linkId → full questionnaire item (includes nested choice add-ons). */
export function buildQuestionnaireItemMap(
  items: QuestionnaireItem[] | undefined,
): Map<string, QuestionnaireItem> {
  const map = new Map<string, QuestionnaireItem>();

  const walk = (nodes: QuestionnaireItem[] | undefined): void => {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.linkId) {
        map.set(node.linkId, node);
      }
      walk(node.item);
    }
  };

  walk(items);
  return map;
}

export function findQuestionnaireItemByLinkId(
  items: QuestionnaireItem[] | undefined,
  linkId: string,
): QuestionnaireItem | undefined {
  return buildQuestionnaireItemMap(items).get(linkId);
}

export function findResponseItemByLinkId(
  responseItems: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem | undefined {
  if (!responseItems) return undefined;
  for (const item of responseItems) {
    if (item.linkId === linkId) return item;
    const nested = findResponseItemByLinkId(item.item, linkId);
    if (nested) return nested;
  }
  return undefined;
}

function responseAnswerMatchesEnableWhen(
  answers: NonNullable<QuestionnaireResponseItem['answer']>,
  condition: QuestionnaireItemEnableWhen,
): boolean {
  if (condition.answerBoolean !== undefined) {
    return answers.some((a) => a.valueBoolean === condition.answerBoolean);
  }
  if (condition.answerCoding?.code) {
    return answers.some((a) => a.valueCoding?.code === condition.answerCoding?.code);
  }
  if (condition.answerString !== undefined) {
    return answers.some((a) => {
      const value = a.valueString ?? a.valueCoding?.display ?? a.valueCoding?.code;
      return value === condition.answerString;
    });
  }
  return false;
}

function selectedAnswerMatchesEnableWhen(
  parent: QuestionnaireItem,
  selectedAnswer: string,
  condition: QuestionnaireItemEnableWhen,
): boolean {
  if (responseAnswerMatchesEnableWhen([{ valueString: selectedAnswer }], condition)) {
    return true;
  }

  if (!condition.answerCoding?.code) {
    return false;
  }

  const { code, display } = condition.answerCoding;
  if (selectedAnswer === code || selectedAnswer === display) {
    return true;
  }

  return (parent.answerOption ?? []).some((opt, idx) => {
    const label = getAnswerOptionDisplayLabel(opt) || `Option ${idx + 1}`;
    if (label !== selectedAnswer) return false;
    return opt.valueCoding?.code === code;
  });
}

/** True when the parent choice answer satisfies the add-on's enableWhen rule. */
export function isChoiceOptionAddonVisible(
  parent: QuestionnaireItem,
  addon: QuestionnaireItem,
  questionnaireResponse: QuestionnaireResponse | undefined,
): boolean {
  const condition = addon.enableWhen?.[0];
  if (!condition || condition.question !== parent.linkId || condition.operator !== '=') {
    return false;
  }

  const parentResponse = findResponseItemByLinkId(questionnaireResponse?.item, parent.linkId ?? '');
  if (!parentResponse?.answer?.length) return false;

  return responseAnswerMatchesEnableWhen(parentResponse.answer, condition);
}

/**
 * Visibility from the UI's current choice value (radio/dropdown label).
 * More reliable than questionnaireResponse alone during preview re-renders.
 */
export function isChoiceOptionAddonVisibleForAnswer(
  parent: QuestionnaireItem,
  addon: QuestionnaireItem,
  selectedAnswer: string,
): boolean {
  if (!selectedAnswer.trim()) return false;
  const condition = addon.enableWhen?.[0];
  if (!condition || condition.question !== parent.linkId || condition.operator !== '=') {
    return false;
  }
  return selectedAnswerMatchesEnableWhen(parent, selectedAnswer, condition);
}

export function isChoiceOptionAddonVisibleNow(
  parent: QuestionnaireItem,
  addon: QuestionnaireItem,
  selectedAnswer: string | undefined,
  questionnaireResponse: QuestionnaireResponse | undefined,
  selectedAnswers?: string[],
): boolean {
  const activeAnswers = (selectedAnswers ?? (selectedAnswer ? [selectedAnswer] : []))
    .map((a) => a.trim())
    .filter(Boolean);

  if (activeAnswers.length > 0) {
    return activeAnswers.some((answer) =>
      isChoiceOptionAddonVisibleForAnswer(parent, addon, answer),
    );
  }
  return isChoiceOptionAddonVisible(parent, addon, questionnaireResponse);
}

export function getChoiceOptionAddonsForItem(
  item: QuestionnaireItem,
  questionnaireItemsByLinkId?: Map<string, QuestionnaireItem>,
): QuestionnaireItem[] {
  const source =
    item.linkId && questionnaireItemsByLinkId?.has(item.linkId)
      ? questionnaireItemsByLinkId.get(item.linkId)!
      : item;
  return getChoiceOptionAddons(source);
}

function buildOptionAddonItem(
  parent: QuestionnaireItem,
  option: QuestionnaireItemAnswerOption,
  optionIndex: number,
  placeholder: string,
): QuestionnaireItem {
  return {
    id: `id-addon-${optionAddonKey(option, optionIndex)}`,
    linkId: buildOptionAddonLinkId(parent.linkId ?? 'q', option, optionIndex),
    type: 'string',
    text: placeholder,
    enableWhen: [buildEnableWhenForOption(parent, option, optionIndex)],
    extension: [
      {
        url: OPTION_ADDON_EXTENSION,
        valueString: optionAddonKey(option, optionIndex),
      },
    ],
  };
}

export function syncOptionAddonEnableWhen(parent: QuestionnaireItem): QuestionnaireItem {
  if (!parent.item?.length) return parent;

  const nextChildren = parent.item.map((child) => {
    if (!isOptionAddonItem(child)) return child;
    const optionIndex = getOptionIndexForAddon(parent, child);
    if (optionIndex < 0) return child;
    const option = parent.answerOption?.[optionIndex];
    if (!option) return child;
    return {
      ...child,
      linkId: buildOptionAddonLinkId(parent.linkId ?? 'q', option, optionIndex),
      enableWhen: [buildEnableWhenForOption(parent, option, optionIndex)],
      extension: [
        {
          url: OPTION_ADDON_EXTENSION,
          valueString: optionAddonKey(option, optionIndex),
        },
      ],
    };
  });

  return { ...parent, item: nextChildren };
}

export function setOptionAddonPlaceholder(
  parent: QuestionnaireItem,
  optionIndex: number,
  placeholder: string | null,
): QuestionnaireItem {
  const option = parent.answerOption?.[optionIndex];
  if (!option) return parent;

  const existing = getOptionAddon(parent, optionIndex);
  const otherChildren = (parent.item ?? []).filter((child) => child !== existing);

  if (placeholder === null) {
    if (!existing) return parent;
    return { ...parent, item: otherChildren.length ? otherChildren : undefined };
  }

  const addon = existing
    ? {
        ...existing,
        text: placeholder,
        enableWhen: [buildEnableWhenForOption(parent, option, optionIndex)],
      }
    : buildOptionAddonItem(parent, option, optionIndex, placeholder);

  return {
    ...parent,
    item: [...otherChildren, addon],
  };
}

export function removeOptionAddon(
  parent: QuestionnaireItem,
  optionIndex: number,
): QuestionnaireItem {
  return setOptionAddonPlaceholder(parent, optionIndex, null);
}

export function removeOptionAddonsForOptionIndices(
  parent: QuestionnaireItem,
  removedIndices: number[],
): QuestionnaireItem {
  let next = parent;
  for (const index of removedIndices.sort((a, b) => b - a)) {
    next = removeOptionAddon(next, index);
  }
  return syncOptionAddonEnableWhen(next);
}
